import { UISref, useRouter } from "@uirouter/react";
import moment from "moment";
import { FunctionComponent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from "../../components/button";
import { Icon } from "../../components/icons";
import { KycIcon } from "../../components/icons/status";
import { Table } from "../../components/table";
import { TableHeaders } from "../../components/table/Head";
import { Widget } from "../../components/widget";
import { Pageable } from "../../services/pageable";
import { AppStore } from "../../store";
import { validationFields } from "../../store/common/models/fields";
import { VerificationActions } from "../../store/verification/actions";
import { VerificationDTO } from "../../store/verification/models/dto/kyc";
import { colours } from "../../styles/colours";
import { NoKyc } from "../kyc-kyb-aml/individual-kyc/all/empty";
import { FraudRoutes } from "../kyc-kyb-aml/routes";

type SingleKyc = VerificationDTO.Responses.GetSingleKyc.T;

interface KycRow {
  userStatus: VerificationDTO.Responses.GetSingleKyc.T["userStatus"];
  createdDate: VerificationDTO.Responses.GetSingleKyc.T["createdDate"];
  fraudCheckId: VerificationDTO.Responses.GetSingleKyc.T["fraudCheckId"];
}

type fieldNames = keyof KycRow;

function useHeaders(): TableHeaders<fieldNames> {
  const createdDate = validationFields.createdDate.useName();

  return useMemo(
    () => [
      {
        name: "createdDate",
        title: createdDate,
      },
      {
        name: "userStatus",
        title: "Status",
      },
    ],
    [createdDate]
  );
}

export const PresentationalAllSelfKyc: FunctionComponent<{
  allSelfKyc: VerificationDTO.Responses.GetAllKyc.T;
}> = ({ allSelfKyc }) => {
  const dispatch = useDispatch();
  const headers = useHeaders();

  const pageableState = useSelector<
    AppStore,
    AppStore["verification"]["allSelfKyc"]["pagination"]
  >((store) => store.verification.allSelfKyc.pagination);

  const onPaginationChange = (
    x: Pageable.T<Exclude<fieldNames, "fraudCheckId">>
  ) => dispatch(VerificationActions.setAllSelfKycPagination(x));

  return (
    <Widget title="My KYC History">
      {allSelfKyc.pageable.totalElements < 1 ? (
        <NoKyc />
      ) : (
        <Table<SingleKyc, fieldNames, "fraudCheckId">
          data={allSelfKyc.content}
          head={headers}
          renderFields={{
            createdDate: ({ createdDate }) => (
              <>{createdDate.local().format("yyyy-MM-DD HH:mm")}</>
            ),
            userStatus: ({ userStatus }) => KycIcon(userStatus, "kyc"),
          }}
          unsortable={["fraudCheckId"]}
          pagination={allSelfKyc.pageable}
          onPaginationChange={onPaginationChange}
          pageableState={pageableState}
        />
      )}
    </Widget>
  );
};
